import { useCallback, useEffect, useState } from "react";
import { Card } from "@/src/components/wrappers/Card";
import { Button } from "@/src/components/ui/button";
import { Input } from "@/src/components/ui/input";
import { fetch } from "@/src/lib/fetch";
import { cn } from "@/src/lib/utils";
import { SupplierBankBody, useBookingSupplierBankMutation } from "@/src/lib/bookingSupplierBankMutation";

/**
 * Supplier bank details (brief §6d): the account we pay the central agent or
 * owner from. Xero settles either an IBAN/SWIFT route or a USD account + ABA
 * routing route, so both are offered and the missing half of whichever route
 * is in use is called out. Details also save against the supplier's company
 * record, so a repeat supplier doesn't need re-keying.
 */

type State = {
  details: SupplierBankBody | null;
  source: "booking" | "company" | null;
  route: "iban" | "usd" | "account" | "none";
  complete: boolean;
  onFile: boolean;
  warnings: string[];
  supplierName: string | null;
  charterCurrency: string | null;
  companyName: string | null;
};

const FIELDS: { key: keyof SupplierBankBody; label: string; route?: "iban" | "usd" }[] = [
  { key: "beneficiaryName", label: "Beneficiary name" },
  { key: "iban", label: "IBAN", route: "iban" },
  { key: "swift", label: "SWIFT / BIC", route: "iban" },
  { key: "accountNumber", label: "Account number", route: "usd" },
  { key: "routingNumber", label: "Routing number (ABA)", route: "usd" },
  { key: "bank", label: "Bank" },
  { key: "currency", label: "Currency" },
  { key: "beneficiaryAddress", label: "Beneficiary address" },
  { key: "reference", label: "Payment reference" },
];

export function SupplierBankSection({ bookingId, supplierLabel }: { bookingId: string; supplierLabel: string }) {
  const [state, setState] = useState<State | null>(null);
  const [editing, setEditing] = useState(false);
  const [route, setRoute] = useState<"iban" | "usd">("iban");
  const [form, setForm] = useState<SupplierBankBody>({});

  const load = useCallback(() => {
    fetch<{ data: State }>(`/api/bookings/${bookingId}/supplier-bank`)
      .then((r: any) => setState(r.data))
      .catch(() => setState(null));
  }, [bookingId]);
  useEffect(load, [load]);

  const mutation = useBookingSupplierBankMutation(bookingId, () => {
    setEditing(false);
    load();
  });

  const startEdit = () => {
    setForm(state?.details ?? {});
    setRoute(state?.route === "usd" ? "usd" : "iban");
    setEditing(true);
  };
  const set = (k: keyof SupplierBankBody, v: string) => setForm((f) => ({ ...f, [k]: v }));

  if (!state) return null;
  const d = state.details;

  return (
    <Card className="flex w-full flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Bank details</h3>
          <p className="text-xs text-slate-500">
            Where we pay {state.supplierName ?? supplierLabel}
            {state.source === "company" && state.companyName ? ` — from ${state.companyName}'s company record` : ""}
          </p>
        </div>
        {!editing && (
          <Button variant="outline" onClick={startEdit}>
            {d ? "Edit" : "Add details"}
          </Button>
        )}
      </div>

      {!editing && !d && (
        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          No bank details on file — this booking's supplier bill can't be paid until they're added.
        </p>
      )}

      {!editing && d && (
        <>
          {state.warnings.length > 0 && (
            <ul className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              {state.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          <dl className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
            {FIELDS.filter((f) => d[f.key]).map((f) => (
              <div key={f.key} className="flex justify-between gap-4 border-b border-slate-100 py-1">
                <dt className="text-sm text-slate-500">{f.label}</dt>
                <dd className="text-sm font-medium">{String(d[f.key])}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {editing && (
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            {(["iban", "usd"] as const).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRoute(r)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium",
                  route === r ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600",
                )}
              >
                {r === "iban" ? "IBAN / SWIFT" : "Account + routing (USD)"}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {FIELDS.filter((f) => !f.route || f.route === route).map((f) => (
              <label key={f.key} className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">{f.label}</span>
                <Input value={String(form[f.key] ?? "")} onChange={(e: any) => set(f.key, e.target.value)} />
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => mutation.mutate(form)} disabled={mutation.isPending}>
              {mutation.isPending ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
